/**
 * faq.ts — Categoria "Perguntas" (10 modelos premium)
 *
 * Blocos de dúvidas frequentes montados em seções com camadas, combinando
 * título, texto de apoio, foto do acervo e chamada para ação.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote B)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

/** Atalho para o bloco de perguntas. */
function perguntas(itens: string, colunas = '1', titulo = '') {
  return b('page.faq', { title: titulo, items: itens, columns: colunas });
}

const GERAIS =
  'Quanto tempo leva a implantação?|Entre 3 e 10 dias, conforme o tamanho da operação.\n' +
  'Preciso instalar algo?|Não. Tudo funciona pelo navegador, no computador ou no celular.\n' +
  'Vocês migram meus dados?|Sim, trazemos seus cadastros do sistema atual sem custo.\n' +
  'Como funciona o suporte?|Atendimento por WhatsApp e e-mail em horário comercial.';

const COMERCIAIS =
  'Tem fidelidade?|Não. Você pode cancelar quando quiser.\n' +
  'Posso trocar de plano?|Sim, a qualquer momento, com cobrança proporcional.\n' +
  'Quais formas de pagamento?|Boleto, cartão de crédito e Pix.';

const modelos: StudioPatternDefinition[] = [
  {
    key: 'faq.lista-simples',
    category: 'faq',
    label: 'Lista de perguntas',
    description: 'Coluna única, leitura fácil e direta.',
    preview: 'faq',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'left', maxWidth: '820px', motion: 'fade' }, [
        b('ui.title', { text: 'Perguntas frequentes', size: 'lg', level: 'h2' }),
        perguntas(GERAIS),
      ]),
    ],
  },

  {
    key: 'faq.duas-colunas',
    category: 'faq',
    label: 'Perguntas em duas colunas',
    description: 'Aproveita a largura da página com duas colunas.',
    preview: 'faq',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        b('ui.title', { text: 'Tire suas dúvidas', size: 'lg', level: 'h2', align: 'center' }),
        perguntas(`${GERAIS}\n${COMERCIAIS}`, '2'),
      ]),
    ],
  },

  {
    key: 'faq.com-foto',
    category: 'faq',
    label: 'Perguntas ao lado da foto',
    description: 'Foto de atendimento à esquerda e perguntas à direita.',
    preview: 'split-right',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade' }, [
        b('ui.image', { src: FOTO.atendimento, alt: 'Equipe de atendimento', ratio: 'portrait', radius: 'xl', shadow: 'lg' }),
        b('ui.title', { text: 'Ainda com dúvida? A gente responde', size: 'lg', level: 'h2' }),
        perguntas(GERAIS),
      ]),
    ],
  },

  {
    key: 'faq.escuro',
    category: 'faq',
    label: 'Perguntas em fundo escuro',
    description: 'Bloco escuro com brilho, bom para fechar a página.',
    preview: 'faq',
    outputs: WEB,
    order: 4,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'xl', align: 'center', maxWidth: '900px', backgroundGradient: 'mesh-dark', glow: 'bottom-left', textColor: '#ffffff', motion: 'fade-up' },
        [
          b('ui.title', { text: 'Perguntas frequentes', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' }),
          perguntas(GERAIS),
        ],
      ),
    ],
  },

  {
    key: 'faq.com-contato',
    category: 'faq',
    label: 'Perguntas com convite',
    description: 'Perguntas seguidas de botão para falar com o time.',
    preview: 'faq',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '860px', motion: 'fade' }, [
        perguntas(GERAIS, '1', 'Perguntas frequentes'),
        b('ui.paragraph', { text: 'Não achou a sua pergunta? Fale com a gente.', align: 'center' }),
        b('ui.buttons', { items: 'Falar com um especialista|#formulario|solid', align: 'center' }),
      ]),
    ],
  },

  {
    key: 'faq.comercial',
    category: 'faq',
    label: 'Dúvidas comerciais',
    description: 'Foco em contrato, pagamento e cancelamento.',
    preview: 'faq',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'left', maxWidth: '820px', backgroundGradient: 'soft', radius: 'xl', motion: 'fade-up' }, [
        b('ui.badge', { text: 'Contrato e pagamento', variant: 'soft' }),
        perguntas(COMERCIAIS),
      ]),
    ],
  },

  {
    key: 'faq.com-selo',
    category: 'faq',
    label: 'Perguntas com selo',
    description: 'Selo de suporte acima da lista de perguntas.',
    preview: 'faq',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '900px', pattern: 'dots', motion: 'fade' }, [
        b('ui.badge', { text: 'Suporte', variant: 'outline', align: 'center' }),
        b('ui.title', { text: 'Tudo o que costumam perguntar', size: 'md', level: 'h2', align: 'center' }),
        perguntas(GERAIS, '2'),
      ]),
    ],
  },

  {
    key: 'faq.com-numeros',
    category: 'faq',
    label: 'Perguntas com prova',
    description: 'Um número de credibilidade acompanha as respostas.',
    preview: 'faq',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.stat', { value: '7 min', label: 'de tempo médio de resposta' }),
        b('ui.paragraph', { text: 'Nosso time responde rápido — e resolve na primeira conversa sempre que possível.' }),
        perguntas(GERAIS),
      ]),
    ],
  },

  {
    key: 'faq.compacta',
    category: 'faq',
    label: 'Perguntas compactas',
    description: 'Versão curta com três perguntas essenciais.',
    preview: 'faq',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'left', maxWidth: '760px', motion: 'fade' }, [perguntas(COMERCIAIS)]),
    ],
  },

  {
    key: 'faq.completa',
    category: 'faq',
    label: 'Central de dúvidas completa',
    description: 'Título, apoio e todas as perguntas em duas colunas.',
    preview: 'faq',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'xl', align: 'center', maxWidth: '1120px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        b('ui.title', { text: 'Central de dúvidas', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.paragraph', { text: 'Reunimos aqui as perguntas que mais recebemos antes da contratação.', align: 'center', maxWidth: '620px' }),
        perguntas(`${GERAIS}\n${COMERCIAIS}`, '2'),
        b('ui.buttons', { items: 'Ainda tenho dúvidas|#formulario|outline', align: 'center' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
